// @file Surface share panel store
import { captureException, captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { getVuexStore } from '@@/bits/pinia'
import PromiseQueue from '@@/bits/promise_queue'
import {
  HttpCode,
  SnackbarNotificationType,
  WallAccessPrivacyType,
  WallAccessRight,
  WallCollaboratorStatus,
} from '@@/enums'
import type {
  CanSetWallPrivacyOption,
  CanSetWallPrivacyOptions,
  Id,
  PrivacyPolicyId,
  UserGroupWallCollaborator,
  UserId,
  UserWallCollaborator,
  WallAccessSettings,
} from '@@/types'
import type { RootState } from '@@/vuexstore/surface/types'
import type { JsonAPIResource } from '@padlet/arvo'
import { fetchJson, HTTPMethod } from '@padlet/fetch'
import { WAVING_HAND_EMOJI } from 'bits/emoji'
import { OzConfirmationDialogBoxButtonScheme } from 'library/v4/components/OzConfirmationDialogBox.vue'
import { defineStore } from 'pinia'
import { useGlobalSnackbarStore } from 'pinia/global_snackbar'
import PadletApi from 'surface/padlet_api'
import { computed, ref } from 'vue'

export enum ShareSubpanel {
  Collaborator = 'collaborator',
  Embed = 'embed',
  Lti1_0 = 'lti1_0',
  ShareViaAnotherApp = 'share_via_another_app',
  PdfExport = 'pdf_export',
  SectionBreakout = 'section_breakout',
  SubmissionRequest = 'submission_request',
}

export const useSurfaceSharePanelStore = defineStore('surfaceSharePanel', () => {
  const surfaceVuexStore = getVuexStore<RootState>() // For gradual conversion to pinia
  const globalSnackbarStore = useGlobalSnackbarStore()
  const { genericFetchError } = globalSnackbarStore
  const queue = new PromiseQueue()

  // Text
  const visitorRightOptions = {
    [WallAccessRight.None]: {
      title: __('No access'),
      description: __('Only collaborators can access this padlet.'),
    },
    [WallAccessRight.Read]: {
      title: __('Reader'),
      description: __('Visitors can read posts, write comments, and add reactions.'),
    },
    [WallAccessRight.Write]: {
      title: __('Writer'),
      description: __('Visitors can write new posts.'),
    },
    [WallAccessRight.Edit]: {
      title: __('Moderator'),
      description: __('Visitors can write, edit, and approve posts.'),
    },
    // On web and the latest mobile app version, you can no longer set the visitor right to Administer.
    // But you can still do so on older mobile app versions, so we still need to allow it on the backend.
    // And we will still need to display it accordingly on the web and latest mobile app version.
    // But this option will not be shown on the new visitor right dropdown,
    // so you won't be able to set it on web and the latest mobile app version.
    [WallAccessRight.Administer]: {
      title: __('Admin'),
      description: __('Visitors can write, edit, and approve posts. Can invite collaborators and modify the padlet.'),
    },
  }
  const standardPrivacyOptions = {
    [WallAccessPrivacyType.Secret]: {
      title: __('Secret'),
      description: __('This padlet will be hidden from the public. Only people with the link can access it.'),
    },
    [WallAccessPrivacyType.PasswordProtected]: {
      title: __('Secret - Password'),
      description: __('Only people with the link and password can access this padlet.'),
    },
    [WallAccessPrivacyType.LoggedInUsersOnly]: {
      title: __('Secret - Log in'),
      description: __('Only logged in visitors with the link can access this padlet.'),
    },
    [WallAccessPrivacyType.Public]: {
      title: __('Public'),
      description: __(
        'Anyone can access this padlet. It will show up on your profile page and in Google search results.',
      ),
    },
  }
  const orgPrivacyOptions = computed(() => {
    const title = __('Org only')
    const description = __('Only member of %{organization} can access this padlet through the link.', {
      organization: surfaceVuexStore?.getters?.tenantName as string,
    })

    return {
      [WallAccessPrivacyType.OrgWideUnlisted]: {
        title,
        description,
      },
      [WallAccessPrivacyType.OrgWideListed]: {
        title,
        description,
      },
    }
  })
  const libraryPrivacyOptions = computed(() => {
    let title = __('Team only')
    if (surfaceVuexStore?.getters?.isClassroomLibrary as boolean) {
      title = __('Classroom only')
    } else if (surfaceVuexStore?.getters?.isSchoolLibrary as boolean) {
      title = __('School only')
    }

    const description = __('Only member of %{organization} can access this padlet through the link.', {
      organization: surfaceVuexStore?.getters?.libraryName as string,
    })
    return {
      [WallAccessPrivacyType.LibraryWideUnlisted]: {
        title,
        description,
      },
      [WallAccessPrivacyType.LibraryWideListed]: {
        title,
        description,
      },
    }
  })
  const allPrivacyOptions = computed(() => {
    return { ...standardPrivacyOptions, ...orgPrivacyOptions.value, ...libraryPrivacyOptions.value }
  })

  // The warning tooltip to show on the Share section header if the privacy is Org or Library only
  const orgOnlyWarningTooltip = computed<string>(() => {
    if (surfaceVuexStore?.getters?.isOrgWall as boolean) {
      return __(
        'This padlet is restricted to users in your organization. Visitors not in your organization will be denied access.',
      )
    }

    if (surfaceVuexStore?.getters?.isLibraryWall as boolean) {
      if (surfaceVuexStore?.getters?.isClassroomLibrary as boolean) {
        return __(
          'This padlet is restricted to users in your classroom. Visitors not in your classroom will be denied access.',
        )
      }

      if (surfaceVuexStore?.getters?.isSchoolLibrary as boolean) {
        return __(
          'This padlet is restricted to users in your school. Visitors not in your school will be denied access.',
        )
      }

      // else should be team library
      return __('This padlet is restricted to users in your team. Visitors not in your team will be denied access.')
    }

    return ''
  })
  // State
  const xSurfaceSharePanel = ref(false)
  const isAwaitingSharePanelApiResponse = ref(true)
  const privacyType = ref(WallAccessPrivacyType.Public)
  const canSetWallPrivacyOptions = ref<CanSetWallPrivacyOption[]>([])
  const visitorRight = ref(WallAccessRight.None)
  const password = ref<string | null>(null)
  const collaborators = ref<UserWallCollaborator[]>([])
  const isAwaitingCollaboratorSearchApiResponse = ref(false)
  const userWallCollaboratorsSearchResult = ref<UserWallCollaborator[]>([])
  const userGroupWallCollaboratorsSearchResult = ref<UserGroupWallCollaborator[]>([])
  const privacyPolicyId = ref<number | null>(null)
  const xQrModal = ref(false)
  const activePanel = ref<ShareSubpanel | null>(null)

  // Getters
  const surfaceExportsScreenshotStatusPath = computed<string>(
    () => surfaceVuexStore?.state.constants.surfaceExportsScreenshotStatusPath,
  )
  const surfaceExportsDocumentStatusPath = computed<string>(
    () => surfaceVuexStore?.state.constants.surfaceExportsDocumentStatusPath,
  )
  const allowedVisitorRights = computed(() => {
    const conditionalRights = {}
    if (canSetPrivacyOption(WallAccessPrivacyType.Private)) {
      conditionalRights[WallAccessRight.None] = visitorRightOptions[WallAccessRight.None]
    }

    const alwaysAllowedRights = {
      [WallAccessRight.Read]: visitorRightOptions[WallAccessRight.Read],
      [WallAccessRight.Write]: visitorRightOptions[WallAccessRight.Write],
      [WallAccessRight.Edit]: visitorRightOptions[WallAccessRight.Edit],
    }

    return { ...conditionalRights, ...alwaysAllowedRights }
  })
  const visitorRightName = computed(() => {
    return visitorRightOptions[visitorRight.value].title
  })
  const xLinkPrivacyRows = computed((): boolean => privacyType.value !== WallAccessPrivacyType.Private) // The private privacy is represented by no access on the visitor right dropdown
  const xPasswordInputRow = computed(
    (): boolean => privacyType.value === WallAccessPrivacyType.PasswordProtected && password.value != null, // When privacy is password-protected, the password that is automatically generated on the backend may not be available yet, wait for it
  )
  const xDashboardToggleRow = computed(
    (): boolean =>
      privacyType.value === WallAccessPrivacyType.OrgWideListed ||
      privacyType.value === WallAccessPrivacyType.OrgWideUnlisted ||
      privacyType.value === WallAccessPrivacyType.LibraryWideListed ||
      privacyType.value === WallAccessPrivacyType.LibraryWideUnlisted,
  )
  const hasCollaboratorSearchResults = computed(
    () => userWallCollaboratorsSearchResult.value.length > 0 || userGroupWallCollaboratorsSearchResult.value.length > 0,
  )
  const allowedOrgPrivacyOptions = computed(() => {
    const options = {}
    if (canSetPrivacyOption(WallAccessPrivacyType.Secret)) {
      options[WallAccessPrivacyType.Secret] = standardPrivacyOptions[WallAccessPrivacyType.Secret]
    }

    if (canSetPrivacyOption(WallAccessPrivacyType.PasswordProtected)) {
      options[WallAccessPrivacyType.PasswordProtected] = standardPrivacyOptions[WallAccessPrivacyType.PasswordProtected]
    }

    // No LoggedInUsersOnly because that is only for native tenant

    // If both OrgWideUnlisted and OrgWideListed are enabled, then we only list OrgWideUnlisted as a dropdown option for Link Privacy.
    // When the user selects OrgWideUnlisted, they can then toggle the "Show in dashboard" toggle from Off to On to get to OrgWideListed.
    //
    // If only OrgWideUnlisted or OrgWideListed is enabled, then we only list that enabled option.
    // When the user selects the enabled option, they cannot toggle the "Show in dashboard" toggle because the other OrgWide option is disabled.
    if (canSetPrivacyOption(WallAccessPrivacyType.OrgWideUnlisted)) {
      options[WallAccessPrivacyType.OrgWideUnlisted] = orgPrivacyOptions.value[WallAccessPrivacyType.OrgWideUnlisted]
    } else if (canSetPrivacyOption(WallAccessPrivacyType.OrgWideListed)) {
      options[WallAccessPrivacyType.OrgWideListed] = orgPrivacyOptions.value[WallAccessPrivacyType.OrgWideListed]
    }

    // No Public because that is only for native tenant

    return options
  })
  const allowedLibraryPrivacyOptions = computed(() => {
    const options = {}
    if (canSetPrivacyOption(WallAccessPrivacyType.Secret)) {
      options[WallAccessPrivacyType.Secret] = standardPrivacyOptions[WallAccessPrivacyType.Secret]
    }

    if (canSetPrivacyOption(WallAccessPrivacyType.PasswordProtected)) {
      options[WallAccessPrivacyType.PasswordProtected] = standardPrivacyOptions[WallAccessPrivacyType.PasswordProtected]
    }

    if (canSetPrivacyOption(WallAccessPrivacyType.LoggedInUsersOnly)) {
      options[WallAccessPrivacyType.LoggedInUsersOnly] = standardPrivacyOptions[WallAccessPrivacyType.LoggedInUsersOnly]
    }

    // If both LibraryWideUnlisted and LibraryWideListed are enabled, then we only list LibraryWideUnlisted as a dropdown option for Link Privacy.
    // When the user selects LibraryWideUnlisted, they can then toggle the "Show in dashboard" toggle from Off to On to get to LibraryWideListed.
    //
    // If only LibraryWideUnlisted or LibraryWideListed is enabled, then we only list that enabled option.
    // When the user selects the enabled option, they cannot toggle the "Show in dashboard" toggle because the other OrgWide option is disabled.
    if (canSetPrivacyOption(WallAccessPrivacyType.LibraryWideUnlisted)) {
      options[WallAccessPrivacyType.LibraryWideUnlisted] =
        libraryPrivacyOptions.value[WallAccessPrivacyType.LibraryWideUnlisted]
    } else if (canSetPrivacyOption(WallAccessPrivacyType.LibraryWideListed)) {
      options[WallAccessPrivacyType.LibraryWideListed] =
        libraryPrivacyOptions.value[WallAccessPrivacyType.LibraryWideListed]
    }

    if (canSetPrivacyOption(WallAccessPrivacyType.Public)) {
      options[WallAccessPrivacyType.Public] = standardPrivacyOptions[WallAccessPrivacyType.Public]
    }

    return options
  })
  const allowedPrivacyOptions = computed(() => {
    if (surfaceVuexStore?.getters?.isOrgWall as boolean) {
      return allowedOrgPrivacyOptions.value
    } else if (surfaceVuexStore?.getters?.isLibraryWall as boolean) {
      return allowedLibraryPrivacyOptions.value
    } else {
      return standardPrivacyOptions
    }
  })
  const privacyTypeName = computed(() => {
    if (privacyType.value === WallAccessPrivacyType.Private) return '' // You can no longer set the Private privacy type, you now set it implicitly when you set visitor right to No Access
    return allPrivacyOptions.value[privacyType.value].title
  })
  const selfSort = (a: UserWallCollaborator, b: UserWallCollaborator): number => {
    if (a.id === currentUserId.value) return -1
    if (b.id === currentUserId.value) return 1
    return 0
  }
  const statusSort = (a: UserWallCollaborator, b: UserWallCollaborator): number => {
    if (a.status === WallCollaboratorStatus.Approved && b.status === WallCollaboratorStatus.Invited) {
      return -1
    }

    if (a.status === WallCollaboratorStatus.Invited && b.status === WallCollaboratorStatus.Approved) {
      return 1
    }

    return 0
  }
  const roleSort = (a: UserWallCollaborator, b: UserWallCollaborator): number => {
    const mapRightToNumber = {
      [WallAccessRight.Read]: 0,
      [WallAccessRight.Write]: 1,
      [WallAccessRight.Edit]: 2,
      [WallAccessRight.Administer]: 3,
    }

    return mapRightToNumber[b.right] - mapRightToNumber[a.right]
  }
  const displayNameSort = (a: UserWallCollaborator, b: UserWallCollaborator): number => {
    // Cannot use ?? because it will only fall-back to the next value if the left-hand side is null or undefined,
    // but we want it to fall-back to the right-hand side even if the left-hand side is an empty string
    const aDisplayName = a.name || a.username || a.email // eslint-disable-line @typescript-eslint/strict-boolean-expressions
    const bDisplayName = b.name || b.username || b.email // eslint-disable-line @typescript-eslint/strict-boolean-expressions
    return aDisplayName.localeCompare(bDisplayName)
  }

  // Sort collaborator list by status (approved then invited), role (greater power first) and then display name (alphabetical sort).
  // Additionally, your own collaborator row will always be at the top.
  const orderedCollaborators = computed<UserWallCollaborator[]>(() => {
    return collaborators.value.sort(displayNameSort).sort(roleSort).sort(statusSort).sort(selfSort)
  })

  const findCollaborator = (collaboratorIdToCheck: Id): UserWallCollaborator | undefined => {
    return collaborators.value.find((collaborator) => collaborator.id === collaboratorIdToCheck)
  }

  const isCollaboratorAlreadyAdded = (collaboratorIdToCheck: Id): boolean => {
    return findCollaborator(collaboratorIdToCheck) !== undefined
  }

  const isCollaboratorAlreadyAddedByEmail = (email: string): boolean => {
    return collaborators.value.find((collaborator) => collaborator?.email === email) !== undefined
  }

  const currentUserId = computed<UserId>(() => {
    return surfaceVuexStore?.state?.user?.id
  })
  const iosAppUrl = computed<string>(() => surfaceVuexStore?.state.constants.iosAppUrl)
  const androidAppUrl = computed<string>(() => surfaceVuexStore?.state.constants.androidAppUrl)
  const iTunesBadgeUrl = computed<string>(() => surfaceVuexStore?.state.constants.itunesBadgeUrl)
  const playStoreBadgeUrl = computed<string>(() => surfaceVuexStore?.state.constants.playStoreBadgeUrl)
  const embedCodeUrlTemplate = computed<string>(() => surfaceVuexStore?.state.constants.embedCodeUrlTemplate)

  // Display on dashboard toggle is only available for LibraryWideUnlisted, LibraryWideListed, OrgWideUnlisted, and OrgWideListed
  // Which are privacy options only available for library and tenanted walls
  const displayOnDashboardName = computed(() => {
    if (surfaceVuexStore?.getters?.isOrgWall as boolean) {
      return __('Display on org dashboard')
    } else if (surfaceVuexStore?.getters?.isClassroomLibrary as boolean) {
      return __('Display on classroom dashboard')
    } else if (surfaceVuexStore?.getters?.isSchoolLibrary as boolean) {
      return __('Display on school dashboard')
    } else {
      return __('Display on team dashboard')
    }
  })
  const isDisplayingOnDashboard = computed(() => {
    if (privacyType.value === WallAccessPrivacyType.LibraryWideListed) return true
    if (privacyType.value === WallAccessPrivacyType.OrgWideListed) return true
    return false
  })
  const toggleDisplayOnDashboardPrivacyMap = {
    [WallAccessPrivacyType.LibraryWideUnlisted]: WallAccessPrivacyType.LibraryWideListed,
    [WallAccessPrivacyType.LibraryWideListed]: WallAccessPrivacyType.LibraryWideUnlisted,
    [WallAccessPrivacyType.OrgWideUnlisted]: WallAccessPrivacyType.OrgWideListed,
    [WallAccessPrivacyType.OrgWideListed]: WallAccessPrivacyType.OrgWideUnlisted,
  }
  const canToggleDisplayOnDashboard = computed(() => {
    const toggleResult = toggleDisplayOnDashboardPrivacyMap[privacyType.value]
    if (toggleResult === undefined) return false
    return canSetPrivacyOption(toggleResult)
  })
  const isSkoletubeUser = computed<boolean>(() => surfaceVuexStore?.state?.isSkoletubeUser as boolean)
  const isLtiConsumable = computed<boolean>(() => {
    return (
      surfaceVuexStore?.state?.tenant?.is_lti_ready === true &&
      surfaceVuexStore?.state?.tenant?.type === 'school' &&
      ['teacher', 'owner'].includes(surfaceVuexStore?.state?.user?.role)
    )
  })
  const ltiCallbackUrl = computed<string>(() => {
    return surfaceVuexStore?.state?.constants?.ltiCallbackUrl
  })
  const ltiConsumerKey = computed<string>(() => {
    return surfaceVuexStore?.state?.constants?.ltiConsumer?.key
  })
  const ltiSharedSecret = computed<string>(() => {
    return surfaceVuexStore?.state?.constants?.ltiConsumer?.sharedSecret
  })
  const baseLtiConfigUrl = computed<string>(() => {
    const baseLtiConfigUrl = surfaceVuexStore?.state?.constants?.baseLtiConfigUrl
    if (baseLtiConfigUrl === undefined) return ''

    const domainName = surfaceVuexStore?.getters?.domainName
    if (domainName === undefined) return ''

    const publicKey = surfaceVuexStore?.getters?.publicKey
    if (publicKey === undefined) return ''

    return baseLtiConfigUrl?.replace('_tenantDomainName_', domainName)?.replace('_wallPublicKey_', publicKey)
  })
  const ltiCustomParamsAsText = computed<string>(() => {
    return `public_key=${surfaceVuexStore?.getters?.publicKey as string}`
  })

  // Actions
  async function fetchWallAccessSettings(): Promise<void> {
    try {
      const response = await PadletApi.WallAccessSettings.fetch(surfaceVuexStore?.getters?.wallId)
      const accessSettings = (response.data as JsonAPIResource<WallAccessSettings>).attributes
      privacyType.value = accessSettings.privacyType
      visitorRight.value = accessSettings.right
      password.value = accessSettings.password
      collaborators.value = accessSettings.collaborators
      privacyPolicyId.value = accessSettings?.privacyPolicyId ?? null
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelFetchWallAccessSettings' })
    }
  }

  async function fetchPartialWallAccessSettings(): Promise<void> {
    try {
      const response = await PadletApi.WallAccessSettings.fetch(surfaceVuexStore?.getters?.wallId)
      const accessSettings = (response.data as JsonAPIResource<WallAccessSettings>).attributes
      privacyType.value = accessSettings.privacyType
      visitorRight.value = accessSettings.right
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelFetchPartialWallAccessSettings' })
    }
  }

  async function fetchWallPrivacyOptions(): Promise<void> {
    try {
      const response = await PadletApi.WallPrivacyOptions.fetchWallPrivacyOptions(surfaceVuexStore?.getters?.wallId)
      canSetWallPrivacyOptions.value = (
        response.data as JsonAPIResource<CanSetWallPrivacyOptions>
      ).attributes.privacyOptions.filter((privacyOption) => privacyOption.canSet)
    } catch (e) {
      genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelFetchWallPrivacyOptions' })
    }
  }

  async function initialize(): Promise<void> {
    isAwaitingSharePanelApiResponse.value = true
    if (surfaceVuexStore?.getters?.canIAdminister as boolean) {
      await Promise.all([fetchWallAccessSettings(), fetchWallPrivacyOptions()])
    } else if (surfaceVuexStore?.getters?.amICollaborator as boolean) {
      // Non-admin collaborator can only fetch partial wall access settings
      await fetchPartialWallAccessSettings()
    }

    isAwaitingSharePanelApiResponse.value = false
  }

  async function updateVisitorRight(
    oldRight: WallAccessRight,
    newRight: WallAccessRight,
    oldPrivacyType: WallAccessPrivacyType,
  ): Promise<void> {
    try {
      const response = await PadletApi.WallAccessSettings.update(surfaceVuexStore?.getters?.wallId, {
        right: newRight,
      })
      const accessSettings = (response.data as JsonAPIResource<WallAccessSettings>).attributes
      visitorRight.value = accessSettings.right
      privacyType.value = accessSettings.privacyType
      password.value = accessSettings.password
    } catch (e) {
      if (e.status === HttpCode.Unauthorized) {
        globalSnackbarStore.setSnackbar({
          notificationType: SnackbarNotificationType.error,
          message: __('Sorry, this option is not allowed. Please reload the page.'),
        })
      } else {
        globalSnackbarStore.setSnackbar({
          notificationType: SnackbarNotificationType.error,
          message: __('Sorry, your request could not be completed. Please try again.'),
        })
        captureFetchException(e, { source: 'SurfaceSharePanelUpdateAccessSettings' })
      }

      visitorRight.value = oldRight
      privacyType.value = oldPrivacyType
    }
  }

  async function updatePrivacyType(
    oldPrivacyType: WallAccessPrivacyType,
    newPrivacyType: WallAccessPrivacyType,
  ): Promise<void> {
    try {
      const response = await PadletApi.WallAccessSettings.update(surfaceVuexStore?.getters?.wallId, {
        privacyType: newPrivacyType,
      })
      const accessSettings = (response.data as JsonAPIResource<WallAccessSettings>).attributes
      visitorRight.value = accessSettings.right
      privacyType.value = accessSettings.privacyType
      password.value = accessSettings.password
    } catch (e) {
      if (e.status === HttpCode.Unauthorized) {
        globalSnackbarStore.setSnackbar({
          notificationType: SnackbarNotificationType.error,
          message: __('Sorry, this option is not allowed. Please reload the page.'),
        })
      } else {
        globalSnackbarStore.setSnackbar({
          notificationType: SnackbarNotificationType.error,
          message: __('Sorry, your request could not be completed. Please try again.'),
        })
        captureFetchException(e, { source: 'SurfaceSharePanelUpdatePrivacyType' })
      }

      privacyType.value = oldPrivacyType
    }
  }

  async function updatePassword(oldPassword: string | null, newPassword: string | null): Promise<void> {
    try {
      const response = await PadletApi.WallAccessSettings.update(surfaceVuexStore?.getters?.wallId, {
        password: newPassword,
      })
      const accessSettings = (response.data as JsonAPIResource<WallAccessSettings>).attributes
      password.value = accessSettings.password
    } catch (e) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Sorry, your request could not be completed. Please try again.'),
      })
      captureFetchException(e, { source: 'SurfaceSharePanelUpdatePassword' })
      password.value = oldPassword
    }
  }

  async function updateCollaborators(
    oldCollaborators: UserWallCollaborator[],
    newCollaborators: UserWallCollaborator[],
    action: string,
  ): Promise<void> {
    try {
      const response = await PadletApi.WallAccessSettings.update(surfaceVuexStore?.getters?.wallId, {
        collaborators: newCollaborators,
      })
      const accessSettings = (response.data as JsonAPIResource<WallAccessSettings>).attributes
      collaborators.value = accessSettings.collaborators
    } catch (e) {
      if (e.status === HttpCode.RateLimitError) {
        // We rate limit collaborator invite to one invite per minute per padlet
        globalSnackbarStore.setSnackbar({
          notificationType: SnackbarNotificationType.error,
          message: __('Please wait before trying again.'),
        })
      } else if (action === 'InviteCollaboratorByEmail' && e.status === HttpCode.Forbidden) {
        // We don't allow invitation of collaborator by email, unless the collaborator is an existing user of the same tenant as the wall
        globalSnackbarStore.setSnackbar({
          notificationType: SnackbarNotificationType.error,
          message: __('Cannot invite users who are not a part of %{tenantName}', {
            tenantName: surfaceVuexStore?.getters?.tenantName as string,
          }),
        })
      } else {
        genericFetchError()
        captureFetchException(e, { source: `SurfaceSharePanel${action}` })
      }

      collaborators.value = oldCollaborators
    }
  }

  async function searchCollaborators(query: string): Promise<void> {
    try {
      isAwaitingCollaboratorSearchApiResponse.value = true
      if (isAppUsing('userGroups')) {
        const response = await PadletApi.WallCollaborator.search({
          searchQuery: encodeURIComponent(query),
          wallId: surfaceVuexStore?.getters?.wallId,
        })

        // Filter group collaborators
        const userGroupWallCollaborators = (response.data as Array<JsonAPIResource<UserGroupWallCollaborator>>)
          .filter(
            (collaborator: JsonAPIResource<UserGroupWallCollaborator>) => collaborator.type === 'userGroupCollaborator',
          )
          .map((collaborator) => collaborator.attributes)

        userGroupWallCollaboratorsSearchResult.value = userGroupWallCollaborators.filter(
          (collaborator) => collaborator.name !== '',
        )

        // Filter user collaborators
        const userWallCollaborators = (response.data as Array<JsonAPIResource<UserWallCollaborator>>)
          .filter((collaborator) => collaborator.type === 'userCollaborator')
          .map((collaborator) => collaborator.attributes)

        userWallCollaboratorsSearchResult.value = userWallCollaborators.filter(
          (collaborator) => collaborator.username !== '',
        )
      } else {
        const response: UserWallCollaborator[] = await fetchJson(
          `/search/collaborators?q=${encodeURIComponent(query)}&limit=10`,
          {
            method: HTTPMethod.get,
          },
        )
        userWallCollaboratorsSearchResult.value = response.filter((collaborator) => collaborator.username !== '')
      }
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceSharePanelSearchCollaborator' })
    } finally {
      isAwaitingCollaboratorSearchApiResponse.value = false
    }
  }

  function clearCollaboratorSearchResults(): void {
    userWallCollaboratorsSearchResult.value = []
    userGroupWallCollaboratorsSearchResult.value = []
  }

  async function resendCollaboratorInvitation(collaboratorId: UserId): Promise<void> {
    if (privacyPolicyId.value === null) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Sorry, your request could not be completed. Please try again.'),
      })
      captureException(new Error('Missing privacyPolicyId'))
    }

    try {
      await PadletApi.WallCollaborator.resendInvitation(privacyPolicyId.value as PrivacyPolicyId, collaboratorId)
      globalSnackbarStore.setSnackbar({
        message: __('Invitation sent!'),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (e) {
      if (e.status === HttpCode.RateLimitError) {
        // Too many requests
        globalSnackbarStore.setSnackbar({
          notificationType: SnackbarNotificationType.error,
          message: __('Please wait before trying again.'),
        })
      } else {
        globalSnackbarStore.setSnackbar({
          notificationType: SnackbarNotificationType.error,
          message: __('Sorry, your request could not be completed. Please try again.'),
        })
        captureFetchException(e, { source: 'SurfaceSharePanelResendCollaboratorInvitation' })
      }
    }
  }

  async function queueUpdateVisitorRight(newRight: WallAccessRight): Promise<void> {
    const oldRight = visitorRight.value
    if (newRight === oldRight) return

    // Optimistically update the visitor right to the new value on the frontend immediately, before queueing the update request to the backend, for faster response
    const oldPrivacyType = privacyType.value
    visitorRight.value = newRight
    if (newRight === WallAccessRight.None) {
      privacyType.value = WallAccessPrivacyType.Private // Optimistically update the privacy to private if setting right to none
    }

    void queue.enqueue('updateAccessSettings', async () => await updateVisitorRight(oldRight, newRight, oldPrivacyType)) // Pass oldRight and oldPrivacyType so we can roll back to those values if the update request fails
  }

  async function queueUpdatePrivacyType(newPrivacyType: WallAccessPrivacyType): Promise<void> {
    const oldPrivacyType = privacyType.value
    if (newPrivacyType === oldPrivacyType) return

    // Optimistically update the privacy type to the new value on the frontend immediately, before queueing the update request to the backend, for faster response
    privacyType.value = newPrivacyType

    void queue.enqueue('updateAccessSettings', async () => await updatePrivacyType(oldPrivacyType, newPrivacyType)) // Pass oldPrivacyType so we can roll back to it if the update request fails
  }

  async function queueUpdatePassword(newPassword: string): Promise<void> {
    const oldPassword = password.value
    if (newPassword === oldPassword) return

    // Optimistically update the password to the new value on the frontend immediately, before queueing the update request to the backend, for faster response
    password.value = newPassword

    void queue.enqueue('updateAccessSettings', async () => await updatePassword(oldPassword, newPassword)) // Pass oldPassword so we can roll back to it if the update request fails
  }

  async function queueUpdateCollaboratorRight(collaboratorId: UserId, newRight: WallAccessRight): Promise<void> {
    const oldCollaborators = collaborators.value
    const newCollaborators = oldCollaborators.map((collaborator) => {
      return collaborator.id === collaboratorId ? { ...collaborator, right: newRight } : collaborator
    })

    // Optimistically update the collaborators to the new value on the frontend immediately, before queueing the update request to the backend, for faster response
    collaborators.value = newCollaborators

    void queue.enqueue(
      'updateAccessSettings',
      async () => await updateCollaborators(oldCollaborators, newCollaborators, 'UpdateCollaboratorRight'),
    ) // Pass oldCollaborators so we can roll back to it if the update request fails
  }

  async function queueRemoveCollaborator(collaboratorId: UserId): Promise<void> {
    const oldCollaborators = collaborators.value
    const newCollaborators = oldCollaborators.filter((collaborator) => collaborator.id !== collaboratorId)

    // Optimistically update the collaborators to the new value on the frontend immediately, before queueing the update request to the backend, for faster response
    collaborators.value = newCollaborators
    globalSnackbarStore.setSnackbar({
      notificationType: SnackbarNotificationType.success,
      message: __('Collaborator removed.'),
    })

    void queue.enqueue(
      'updateAccessSettings',
      async () => await updateCollaborators(oldCollaborators, newCollaborators, 'RemoveCollaborator'),
    ) // Pass oldCollaborators so we can roll back to it if the update request fails
  }

  async function queueInviteCollaborator(
    collaborator: UserWallCollaborator | UserGroupWallCollaborator,
  ): Promise<void> {
    if (isCollaboratorAlreadyAdded(collaborator.id)) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: __('%{name} is already added as collaborator.', { name: collaborator.name }),
      })
    }

    const oldCollaborators = collaborators.value
    const newCollaborator = {
      ...collaborator,
      right: visitorRight.value === WallAccessRight.None ? WallAccessRight.Read : visitorRight.value,
      status: WallCollaboratorStatus.Invited,
    }

    const newCollaborators = oldCollaborators.concat(newCollaborator as UserWallCollaborator)

    // Optimistically update the collaborators to the new value on the frontend immediately, before queueing the update request to the backend, for faster response
    collaborators.value = newCollaborators

    void queue.enqueue(
      'updateAccessSettings',
      async () => await updateCollaborators(oldCollaborators, newCollaborators, 'InviteCollaborator'),
    ) // Pass oldCollaborators so we can roll back to it if the update request fails
  }

  async function queueInviteCollaboratorByEmail(email: string): Promise<void> {
    if (isCollaboratorAlreadyAddedByEmail(email)) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: __('%{name} is already added as collaborator.', { name: email }),
      })
    }

    const oldCollaborators = collaborators.value
    const newCollaborator: Partial<UserWallCollaborator> = {
      email: email.trim().toLowerCase(),
      right: visitorRight.value === WallAccessRight.None ? WallAccessRight.Read : visitorRight.value,
      status: WallCollaboratorStatus.Invited,
    }

    const newCollaborators = oldCollaborators.concat(newCollaborator as UserWallCollaborator)

    // Optimistically update the collaborators to the new value on the frontend immediately, before queueing the update request to the backend, for faster response
    collaborators.value = newCollaborators

    void queue.enqueue(
      'updateAccessSettings',
      async () => await updateCollaborators(oldCollaborators, newCollaborators, 'InviteCollaboratorByEmail'),
    ) // Pass oldCollaborators so we can roll back to it if the update request fails
  }

  async function toggleDisplayOnDashboard(): Promise<void> {
    await queueUpdatePrivacyType(toggleDisplayOnDashboardPrivacyMap[privacyType.value])
  }

  function canSetPrivacyOption(privacyType: WallAccessPrivacyType): boolean {
    return (
      canSetWallPrivacyOptions.value.find(
        (option: CanSetWallPrivacyOption) => (option.name as string) === (privacyType as string),
      ) !== undefined
    )
  }

  function confirmCollaboratorLeavePadlet(): void {
    const adminOrAdministrator = surfaceVuexStore?.getters?.isLibraryWall === true ? 'admin' : 'administrator'
    void surfaceVuexStore?.dispatch(
      'confirmationDialog/openConfirmationDialog',
      {
        ...WAVING_HAND_EMOJI,
        isCodeProtected: false,
        title: __('Leave this padlet?'),
        body: __('You will have to be invited back by an %{adminOrAdministrator}.', { adminOrAdministrator }),
        confirmButtonText: __('Leave'),
        cancelButtonText: __('Nevermind'),
        afterConfirmActions: [{ type: 'share/leavePadlet' }],
        buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
        xShadow: true,
      },
      { root: true },
    )
  }

  return {
    // State
    xSurfaceSharePanel,
    isAwaitingSharePanelApiResponse,
    privacyType,
    visitorRight,
    password,
    collaborators,
    isAwaitingCollaboratorSearchApiResponse,
    userWallCollaboratorsSearchResult,
    userGroupWallCollaboratorsSearchResult,
    canSetWallPrivacyOptions,
    xQrModal,
    activePanel,

    // Getters
    surfaceExportsScreenshotStatusPath,
    surfaceExportsDocumentStatusPath,
    xLinkPrivacyRows,
    xPasswordInputRow,
    xDashboardToggleRow,
    hasCollaboratorSearchResults,
    allowedVisitorRights,
    visitorRightName,
    orderedCollaborators,
    currentUserId,
    iosAppUrl,
    androidAppUrl,
    iTunesBadgeUrl,
    playStoreBadgeUrl,
    embedCodeUrlTemplate,
    allowedPrivacyOptions,
    orgOnlyWarningTooltip,
    privacyTypeName,
    displayOnDashboardName,
    isDisplayingOnDashboard,
    canToggleDisplayOnDashboard,
    isSkoletubeUser,
    isLtiConsumable,
    ltiCallbackUrl,
    ltiConsumerKey,
    ltiSharedSecret,
    baseLtiConfigUrl,
    ltiCustomParamsAsText,

    // Actions
    initialize,
    searchCollaborators,
    resendCollaboratorInvitation,
    queueUpdateVisitorRight,
    queueUpdatePrivacyType,
    queueUpdatePassword,
    queueUpdateCollaboratorRight,
    queueRemoveCollaborator,
    queueInviteCollaborator,
    queueInviteCollaboratorByEmail,
    toggleDisplayOnDashboard,
    confirmCollaboratorLeavePadlet,
    clearCollaboratorSearchResults,
    findCollaborator,
  }
})
